import { FC, memo, MouseEvent, useState } from "react";
import styled from "styled-components";
import { AnimatePresence } from "framer-motion";

import { Td } from "@/components/Table";
import Text from "@/components/Text";
import { ReactComponent as HyperlinkIcon } from "@/assets/icons/hyperlink.svg";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { useQueryClient } from "@tanstack/react-query";
import TipForm from "./TipForm";
import { VideoTipResource } from "@/types/resources";
import { durationFormatter } from "@/utils/helpers";

const HyperLink = styled("a").attrs({
  rel: "noreferrer noopener",
  target: "_blank",
})`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  svg {
    margin-right: 6px;
    width: 14px;
    flex-shrink: 0;
  }
  > span {
    overflow-x: hidden;
    text-overflow: ellipsis;
    line-height: 2;
  }
`;

const HyperLinkTd = styled(Td)`
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionsTd = styled(Td)`
  > div {
    position: relative;
    z-index: 2;
  }
`;

interface Props {
  tip: VideoTipResource;
}

const TipRow: FC<Props> = memo(({ tip }) => {
  const [showEditForm, setShowEditForm] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();
      const confirmed = await alerts.confirm(
        "Are you sure you would like to delete this tip?",
        `${tip.external_id} - ${tip.name}`
      );

      if (!confirmed) return;

      await api.delete(`/v1/video-tips/${tip.id}`);

      queryClient.invalidateQueries(["video_tips"], {
        exact: false,
      });

      alerts.success("Tip successfully deleted");
    } catch (_) {
      alerts.error();
    }
  };

  return (
    <tr>
      <Td>{tip.external_id}</Td>
      <Td>
        <Text $type="smallHeadline">{tip.name}</Text>
      </Td>
      <HyperLinkTd>
        {tip.thumbnail ? (
          <HyperLink href={tip.thumbnail}>
            <HyperlinkIcon />
            <span>{tip.thumbnail}</span>
          </HyperLink>
        ) : (
          "-"
        )}
      </HyperLinkTd>
      <Td>{durationFormatter(tip.duration)}</Td>
      <HyperLinkTd>
        {tip.embed_link ? (
          <HyperLink href={tip.embed_link}>
            <HyperlinkIcon />
            <span>{tip.embed_link}</span>
          </HyperLink>
        ) : (
          "-"
        )}
      </HyperLinkTd>
      <Td>{tip.video_type}</Td>
      <Td>{tip.category}</Td>
      <Td>{tip.subcategory ?? "-"}</Td>
      <Td>{tip.mashup ? "Yes" : "No"}</Td>
      <ActionsTd>
        <ActionsDropdown>
          <ActionsDropdownButton
            as="button"
            onClick={() => setShowEditForm(true)}
          >
            <PencilIcon />
            Edit
          </ActionsDropdownButton>
          <ActionsDropdownButton as="button" onClick={onDelete}>
            <TrashIcon />
            Delete
          </ActionsDropdownButton>
        </ActionsDropdown>
        <AnimatePresence>
          {showEditForm && (
            <TipForm editableTip={tip} onClose={() => setShowEditForm(false)} />
          )}
        </AnimatePresence>
      </ActionsTd>
    </tr>
  );
});

export default TipRow;
